import { FC, useState, useCallback } from 'react'
import { Card, Row, Col, Space, Button, message, Tag } from 'antd'
import {
  ExportOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons'
import moment from 'moment-timezone'
import { DateSelector } from '@/components/DailySalesSummary/DateSelector'
import { CustomerSalesTable } from '@/components/DailySalesSummary/CustomerSalesTable'
import { PaymentSummaryDisplay } from '@/components/DailySalesSummary/PaymentSummaryDisplay'
import { RemainingCashInput } from '@/components/DailySalesSummary/RemainingCashInput'
import { ExpenseSection } from '@/components/DailySalesSummary/ExpenseSection'
import { useDailySalesData, AutoSaveStatus } from '@/hooks/useDailySalesData'
import { useExpenseManagement } from '@/hooks/useExpenseManagement'
import { excelExportService } from '@/services/excelExportService'
import { toggleVoidOrder } from '@/supabase/Orderbook/orderbook'

export const DailySalesSummaryPage: FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format('YYYY-MM-DD')
  )

  const {
    customerSalesData,
    paymentSummary,
    loading,
    updating,
    error,
    refreshData,
    updateRemainingCashAmount,
    hasLocalChanges,
    dailySalesData,
    addPaymentEntry,
    updatePaymentEntry,
    deletePaymentEntry,
    localPaymentEntries,
    hasPaymentChanges,
    autoSaveStatus,
  } = useDailySalesData(selectedDate)
  
  const {
    expenses,
    loading: expensesLoading,
    updating: expensesUpdating,
    addExpense,
    editExpense,
    removeExpense,
  } = useExpenseManagement(selectedDate)

  const handleDateChange = useCallback(
    (date: string) => {
      setSelectedDate(date)
    },
    []
  )

  const handleExportExcel = async () => {
    if (!dailySalesData) {
      message.warning('ไม่มีข้อมูลสำหรับส่งออก', 2)
      return
    }

    if (hasLocalChanges) {
      message.warning('กรุณาบันทึกการเปลี่ยนแปลงก่อนส่งออก Excel', 3)
      return
    }

    try {
      await excelExportService.generateAndDownload(dailySalesData)
      message.success('ส่งออกไฟล์ Excel สำเร็จ', 2)
    } catch (error) {
      console.error('Error exporting Excel:', error)
      message.error('เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel', 3)
    }
  }

  const handleToggleVoid = useCallback(async (orderId: number, isVoided: boolean) => {
    const success = await toggleVoidOrder(orderId, isVoided)
    if (success) {
      await refreshData()
    } else {
      message.error('เกิดข้อผิดพลาดในการเปลี่ยนสถานะรายการ', 3)
    }
  }, [refreshData])

  const renderAutoSaveStatus = (status: AutoSaveStatus) => {
    switch (status) {
      case 'pending':
        return <Tag icon={<CloudSyncOutlined />} color="warning">รอบันทึก...</Tag>
      case 'saving':
        return <Tag icon={<LoadingOutlined spin />} color="processing">กำลังบันทึก...</Tag>
      case 'saved':
        return <Tag icon={<CheckCircleOutlined />} color="success">บันทึกแล้ว</Tag>
      case 'error':
        return <Tag icon={<CloseCircleOutlined />} color="error">บันทึกไม่สำเร็จ</Tag>
      default:
        return null
    }
  }

  return (
    <div
      className='space-y-4 md:space-y-6 p-2 md:p-4 lg:p-6 w-full'
      style={{ maxWidth: '100vw', overflowX: 'hidden' }}
    >
      {/* Header */}
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
        <h1 className='text-2xl md:text-3xl font-bold text-gray-900'>
          Daily Sales Summary
        </h1>
        <Space wrap>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExportExcel}
            loading={loading}
            disabled={updating || !dailySalesData || hasLocalChanges}
            size='large'
          >
            Export Data
          </Button>
        </Space>
      </div>

      {/* Error Display - Only for main data, not expenses */}
      {error && (
        <Card className='border-red-200 bg-red-50'>
          <div className='text-red-600'>
            <strong>เกิดข้อผิดพลาด:</strong> {error}
          </div>
        </Card>
      )}

      {/* Date Selection */}
      <DateSelector
        onDateChange={handleDateChange}
        loading={loading || updating}
      />

      {/* Customer Sales Table */}
      <div style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <Card
          title='ข้อมูลการขายลูกค้า'
          className='w-full'
          extra={renderAutoSaveStatus(autoSaveStatus)}
        >
          <CustomerSalesTable
            customers={customerSalesData}
            loading={loading}
            addPaymentEntry={addPaymentEntry}
            updatePaymentEntry={updatePaymentEntry}
            deletePaymentEntry={deletePaymentEntry}
            localPaymentEntries={localPaymentEntries}
            hasPaymentChanges={hasPaymentChanges}
            onToggleVoid={handleToggleVoid}
          />
        </Card>
      </div>

      {/* Payment Summary and Remaining Cash */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={16} lg={16} xl={16} xxl={16}>
          <PaymentSummaryDisplay
            paymentSummary={paymentSummary}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
          <RemainingCashInput
            value={paymentSummary.remainingCashTHB}
            onChange={updateRemainingCashAmount}
            loading={loading || updating}
          />
        </Col>
      </Row>

      {/* Expenses */}
      <Card title='ค่าใช้จ่าย'>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <ExpenseSection
              expenseType='FUEL'
              expenses={expenses.FUEL}
              onAddExpense={(name, amount) => addExpense('FUEL', name, amount)}
              onUpdateExpense={editExpense}
              onDeleteExpense={removeExpense}
              loading={expensesLoading || expensesUpdating}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <ExpenseSection
              expenseType='EMPLOYEE_OVERTIME'
              expenses={expenses.EMPLOYEE_OVERTIME}
              onAddExpense={(name, amount) =>
                addExpense('EMPLOYEE_OVERTIME', name, amount)
              }
              onUpdateExpense={editExpense}
              onDeleteExpense={removeExpense}
              loading={expensesLoading || expensesUpdating}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
            <ExpenseSection
              expenseType='OTHER'
              expenses={expenses.OTHER}
              onAddExpense={(name, amount) => addExpense('OTHER', name, amount)}
              onUpdateExpense={editExpense}
              onDeleteExpense={removeExpense}
              loading={expensesLoading || expensesUpdating}
            />
          </Col>
        </Row>
      </Card>
    </div>
  )
}
